import React, { useState, useEffect, useRef } from 'react';
import { loadCheckoutWebComponents } from '@checkout.com/checkout-web-components';
import { useTranslation } from 'react-i18next';
import { Box, Button, Container, Dialog, DialogContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useParams } from 'react-router';
import { useLocation, useNavigate } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import KuwaitiDinarFormat from '../../components/KuwaitiDinarFormat';
import { RootState } from '../../Redux/reducer';
import { useDispatch, useSelector } from 'react-redux';
import { apiURL } from '../../constants/apiUrlConstants';
import { setCivilIdAction } from '../../Redux/actions';
import IdleTimerContainer from '../../components/IdleTimerContainer';
import Loader from '../../components/Loader';
import axios, { AxiosError } from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import { clearSessionStorage, getNewToken, postRequest } from '../../services/httpservice';
import 'react-toastify/dist/ReactToastify.css';
import CloseIcon from "@mui/icons-material/Close";
import IconButton from '@mui/material/IconButton';
import { Console } from 'console';
import knetLogo from "../../assets/knet.png";
const label = { inputProps: { 'aria-label': 'Checkbox demo' } };

interface InstallmentDetails {
  installmentNo: string;
  dueDate: string;
  installAmount: string;
  totalAmount: string;
  selected: boolean;
  selectedNew: boolean;
}

interface ApiResponse {
  civilId: string;
  loanAccountNo: string | null;
  type: string | null;
  takliefCharges: string;
  legalCharges: string;
  debitNoteCharges: string;
  status: string;
  message: string;
  dpd: string;
  dpdValue: string;
  customerNo: string;
  installmentDetails: InstallmentDetails[];
  cardNo: string;
}
interface ChangeCardModalProps {
  open: boolean;
  onClose: () => void;
  onContinueRegistered: () => void;
  cardNo: string;
  isLoading: boolean;
  civilId: string | null;
  loanAccountNo: string;
  totalAmount: number;
}
interface InstallmentsTableProps {
  onTotalAmountChange: (totalAmount: number) => void;
}
const ChangeCardModal = ({ open, onClose, onContinueRegistered, cardNo, isLoading: parentLoading, civilId, loanAccountNo, totalAmount }: ChangeCardModalProps) => {
  const { t, i18n } = useTranslation();
  const getLocalizedMessage = (message: string) => {
    const parts = message.split("/");
    if (i18n.language === "ar" && parts.length > 1) {
      return parts[1].trim();
    }
    return parts[0].trim();
  };
  const navigate = useNavigate();
  const [selected, setSelected] = useState<"kd" | "installment">("installment");
  const [knetLoading, setKnetLoading] = useState(false);
  const [showKnet, setShowKnet] = useState(false);
  const [knetSessionData, setKnetSessionData] = useState<any>(null);
  const knetContainerRef = useRef<HTMLDivElement | null>(null);
  const knetComponentRef = useRef<any>(null);

  // Reset all knet state when modal closes
  useEffect(() => {
    if (!open) {
      setShowKnet(false);
      setKnetSessionData(null);
      setKnetLoading(false);
      setSelected("installment");
      try { knetComponentRef.current?.unmount?.(); } catch { }
      knetComponentRef.current = null;
    }
  }, [open]);

  // Mount knet component (hidden) once session data is ready
  useEffect(() => {
    if (!showKnet || !knetSessionData) return;
    const timer = setTimeout(async () => {
      if (!knetContainerRef.current) return;
      let cancelled = false;
      try {
        const checkout = await loadCheckoutWebComponents({
          publicKey: process.env.REACT_APP_CHECKOUT_PUBLIC_KEY as string,
          environment: process.env.REACT_APP_CHECKOUT_BASE_URL as any,
          locale: 'en-GB',
          paymentSession: knetSessionData.sessionResponse,
          onReady: () => {
            knetComponentRef.current?.submit?.();
          },
          onPaymentCompleted: (_comp: any, _res: any) => {
            onClose();
            navigate('/home/payment-status');
          },
          onError: (_comp: any, _err: any) => {
            setKnetLoading(false);
          },
        });
        if (cancelled || !knetContainerRef.current) return;
        const knetComp = checkout.create('knet');
        const available = await knetComp.isAvailable();
        if (!available) {
          setKnetLoading(false);
          toast.error('KNET payment is not available for this session.');
          return;
        }
        knetComponentRef.current = knetComp;
        knetComp.mount(knetContainerRef.current);
      } catch (e: any) {
        if (!cancelled) {
          setKnetLoading(false);
          toast.error(e?.message || process.env.REACT_APP_ERROR_MSG);
        }
      }
      return () => { cancelled = true; };
    }, 50);
    return () => {
      clearTimeout(timer);
      try { knetComponentRef.current?.unmount?.(); } catch { }
      knetComponentRef.current = null;
    };
  }, [showKnet, knetSessionData]);

  const handleContinue = async () => {
    if (selected === 'installment') {
      onContinueRegistered();
      return;
    }
    // KNET: create session → mount component silently
    try {
      setKnetLoading(true);
      const response = await postRequest(
        process.env.REACT_APP_API_URL + apiURL.CREATE_KNET_SESSION,
        { civilId, loanAccountNo, paymentType: 'KNET', amount: totalAmount }
      );
      if (response === null) { setKnetLoading(false); toast.dismiss(); toast.error(process.env.REACT_APP_ERROR_MSG); return; }
      if (response.status === 401) { setKnetLoading(false); toast.error('Session expired'); clearSessionStorage(); setTimeout(() => navigate('/'), 2000); return; }
      if (response.data === '') { setKnetLoading(false); toast.dismiss(); toast.error(process.env.REACT_APP_ERROR_MSG); return; }
      const data = response.data;
      if (data.status === 'S' && data.sessionResponse) {
        setKnetSessionData(data);
        setShowKnet(true);
        // loader stays until onReady fires
      } else if (data.status === 'E') {
        setKnetLoading(false);
        onClose();
        toast.error(data.message ? getLocalizedMessage(data.message) : process.env.REACT_APP_ERROR_MSG);
      } else {
        setKnetLoading(false);
        onClose();
        toast.error(data.message || process.env.REACT_APP_ERROR_MSG);
      }
    } catch (error) {
      setKnetLoading(false);
      onClose();
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 401) {
          toast.error('Session expired'); clearSessionStorage(); setTimeout(() => navigate('/'), 2000);
        } else {
          toast.error(process.env.REACT_APP_ERROR_MSG);
        }
      }
    }
  };

  const isLoading = parentLoading || knetLoading;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{

        sx: {
          width: "563px",
          height: "398px",
          borderRadius: "10px",
          p: "52px 32px",
          overflow: "hidden",


        },
      }}
    >
      {isLoading && <Loader />}
      <DialogContent sx={{
        padding: "0 !important",
        flex: "unset",
        overflowY: "unset"
      }}>
        {/* close button */}
        <IconButton
          onClick={onClose}
          // size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "#333",
          }}
        >
          <CloseIcon sx={{

            fontSize: "20.63px",
            strokeWidth: 0.6, // may not affect Material Icons
          }} />
        </IconButton>

        <Typography sx={{ fontWeight: 700, fontSize: "18px", color: "#111", mb: 3, pr: 3, lineHeight: 1.4 }}>
          {t('installments.choosePaymentMethod')}
        </Typography>

        {/* KNET option */}
        <Box
          onClick={() => !showKnet && setSelected("kd")}
          sx={{
            border: selected === "kd" ? "2px solid #2493C7" : "1.5px solid #D0D0D0",
            borderRadius: "4px", p: "27px  27px  ", mb: "16px",
            cursor: showKnet ? "default" : "pointer",
            background: selected === "kd" ? "#EAF5FB" : "#fff",
            transition: "all 0.18s",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
            <Box component="img" src={knetLogo} alt="KNET" sx={{ width: 38, height: 29 }} />
            <Typography sx={{ fontWeight: selected === "kd" ? 600 : 400, color: selected === "kd" ? "#2493C7" : "#111", fontSize: "15px" }}>
              KNET
            </Typography>
          </Box>
        </Box>

        {/* Registered Card option */}
        <Box
          onClick={() => !showKnet && setSelected("installment")}
          sx={{
            border: selected === "installment" ? "2px solid #2493C7" : "1.5px solid #D0D0D0",
            borderRadius: "4px", p: "12px 12px", mb: "32px",
            cursor: showKnet ? "default" : "pointer",
            background: selected === "installment" ? "#EAF5FB" : "#fff",
            transition: "all 0.18s", textAlign: "center",
          }}
        >
          <Typography sx={{ fontWeight: selected === "installment" ? 600 : 400, color: selected === "installment" ? "#2493C7" : "#111", fontSize: "14px" }}>
            {t('installments.registeredCard')}  <Box
              component="span"
              sx={{
                display: "block",
                mt: 1, // spacing between lines
              }}
            >
              {cardNo}
            </Box>
          </Typography>
        </Box>

        {/* Hidden knet component container — mounted silently, submit() called on button click */}
        <div ref={knetContainerRef} id="knet-container" style={{ display: 'none' }} />

        <Button
          fullWidth
          variant="contained"
          disabled={isLoading}
          onClick={handleContinue}
          sx={{
            background: "#2493C7",
            fontWeight: 500,
            fontSize: "14px",
            padding: "6px 16px",
            borderRadius: "4px",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            boxShadow: "none",
            "&:hover": { background: "#1a7aaa", boxShadow: "none" },
          }}
        >
          {t('installments.continue')}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
const Installments = () => {
  const { t, i18n } = useTranslation();
  const getLocalizedMessage = (message: string) => {
    const parts = message.split("/");
    if (i18n.language === "ar" && parts.length > 1) {
      return parts[1].trim();
    }
    return parts[0].trim();
  };
  //const { loanNumber, type } = useParams<{ loanNumber?: string; type?: string }>();
  const location = useLocation();
  const { loanAccountNo, type } = location.state || {};
  const [civilId, setLocalCivilId] = useState<string | null>(null);
  const [TypeState, setType] = useState<string | null>(type);
  const civilIdStore = useSelector((state: RootState) => state.civilId);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<InstallmentDetails[]>([]);
  const [installmentList, setInstallmentList] = useState<InstallmentDetails[]>([]);
  const [dpdInstallmentList, setDPDInstallmentList] = useState<InstallmentDetails[]>([]);
  const [normalInstallmentList, setNormalInstallmentList] = useState<InstallmentDetails[]>([]);
  const [installmentData, setInstallmentData] = useState<ApiResponse | null>(null);
  const [subTotalAmount, setsubTotalAmount] = useState<number>(0);
  const [TotalAmount, setTotalAmount] = useState<number>(0);
  const [Charges, setCharges] = useState<number>(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsloading] = useState<boolean>(true)
  const [customerNo, setCustomerNo] = useState<string | null>(null);
  const [toastCloseInMs, setToastCloseInMs] = useState<number>(0);
  const [name, setName] = useState<string>('');
  const [nameArabic, setNameArabic] = useState<string>('');
  const [cardNo, setCardNo] = useState<string>('');
  useEffect(() => {
    const name = sessionStorage.getItem('name');
    const nameArabic = sessionStorage.getItem('nameArabic');
    if (name) {
      setName(name);
    }
    if (nameArabic !== null && nameArabic !== "" && nameArabic !== undefined && nameArabic !== "null") {
      if (name !== null && name !== "" && name !== undefined && name !== "null") {
        setName(name + " / ");
      }
      setNameArabic(nameArabic);
    }
    const msToast = process.env.REACT_APP_TOAST_CLOSE_IN_SECONDS;
    if (msToast) {
      setToastCloseInMs(parseInt(msToast));
    }
    const accessToken = sessionStorage.getItem('accessToken');
    if (!accessToken) {
      navigate('/');
      return;
    }

    const storedCivilId = sessionStorage.getItem('civilId');
    if (storedCivilId) {
      setLocalCivilId(storedCivilId);
      dispatch(setCivilIdAction(storedCivilId));
      if (storedCivilId !== null && storedCivilId !== "" && storedCivilId !== undefined &&
        loanAccountNo !== null && loanAccountNo !== "" && loanAccountNo !== undefined) {
        getTransactionDetails(storedCivilId, loanAccountNo);
      }
      if (type !== null && type !== "" && type !== undefined) {
        setType(type);
      }
    } else if (civilIdStore) {
      setLocalCivilId(civilIdStore);
    }
  }, [civilIdStore, loanAccountNo, type, dispatch, navigate]);

  useEffect(() => {
    if (civilId) {
      sessionStorage.setItem('civilId', civilId);
    }
  }, [civilId]);

  const getTransactionDetails = async (civilId: string, loanAccountNo: string) => {
    try {
      const accessToken = sessionStorage.getItem('accessToken');
      if (!accessToken) {
        clearSessionStorage();
        navigate('/');
        return;
      }

      if (accessToken) {
        const response = await postRequest(
          process.env.REACT_APP_API_URL + apiURL.GET_INSTALLMENTS, {
          civilId: civilId,
          loanAccountNo: loanAccountNo
        }
        );

        setIsloading(false);

        if (response === null) {
          toast.dismiss();
          toast.error(process.env.REACT_APP_ERROR_MSG);
          return;
        }
        if (response.status === 401) {
          toast.error('Session expired');
          clearSessionStorage();
          setTimeout(() => {
            navigate('/');
          }, 2000);
          return;
        }
        if (response.data === '') {
          toast.dismiss();
          toast.error(process.env.REACT_APP_ERROR_MSG);
          return;
        }

        const data = response.data;
        setInstallmentData(data);
        setCardNo(data.cardNo || '');
        if (data.installmentDetails != null && data.installmentDetails.length > 0) {
          setInstallmentList(data.installmentDetails);
          const dpdInstallments = data.installmentDetails.filter((i: InstallmentDetails) => i.selected === true);
          setDPDInstallmentList(dpdInstallments);
          const normaldInstallments = data.installmentDetails.filter((i: InstallmentDetails) => i.selected !== true);
          setNormalInstallmentList(normaldInstallments);
        }
        setCustomerNo(data.customerNo)

        // if (Number(data.legalCharges) > 0) {
        //   setCharges(Number(data.legalCharges));
        // }
        // if (Number(data.takliefCharges) > 0) {
        //   setCharges(Number(data.takliefCharges));
        // }
        // if (Number(data.debitNoteCharges) > 0) {
        //   setCharges(Number(data.debitNoteCharges));
        // }

        // Accumulate all types of charges
        let totalCharges = 0;
        if (Number(data.legalCharges) > 0) {
          totalCharges += Number(data.legalCharges);
        }
        if (Number(data.takliefCharges) > 0) {
          totalCharges += Number(data.takliefCharges);
        }
        if (Number(data.debitNoteCharges) > 0) {
          totalCharges += Number(data.debitNoteCharges);
        }

        // Set the accumulated charges
        setCharges(totalCharges);

        setIsloading(false);
        getNewToken();
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        setIsloading(false);
        const axiosError = error as AxiosError;
        if (axiosError.response) {
          if (axiosError?.response.status == 401) {
            toast.error('Session expired');
            clearSessionStorage();
            setTimeout(() => {
              navigate('/');
            }, 2000);
          } else {
            toast.error(process.env.REACT_APP_ERROR_MSG);
          }
        }
      }
    }
  }

  const toggleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    if (newSelectAll) {
      if (dpdInstallmentList.length > 0) {
        setSelectedRows([...normalInstallmentList]);
      } else {
        setSelectedRows([...installmentList]);
      }
    } else {
      installmentList.forEach((row) => {
        row.selectedNew = false;
      });
      setSelectedRows([]);
    }
  };

  const handleCheckboxChange = (row: InstallmentDetails) => {
    const isSelected = selectedRows.some((selectedRow) => selectedRow.installmentNo === row.installmentNo);
    if (isSelected) {
      // The checkbox is only clickable for the highest-numbered selected
      // installment (lower ones are disabled), so unchecking only ever
      // needs to remove this single row, not cascade.
      row.selectedNew = false;
      setSelectedRows(selectedRows.filter((selectedRow) => selectedRow.installmentNo !== row.installmentNo));
    } else {
      const filterInstallments = installmentList.filter(r => parseInt(r.installmentNo) < parseInt(row.installmentNo) && r.selected !== true);
      if (filterInstallments != null && filterInstallments.length > 0) {
        filterInstallments.forEach((rowItem) => {
          rowItem.selectedNew = true;
        })
      }
      filterInstallments.push(row);
      setSelectedRows(filterInstallments);
    }
  };

  useEffect(() => {
    calculateTotalAmount();
  }, [selectedRows, Charges, dpdInstallmentList, selectedRows]);

  useEffect(() => {
    if (normalInstallmentList.length === 0) {
      return;
    }
    const allSelected = normalInstallmentList.every((row) =>
      row.selectedNew || selectedRows.some((selectedRow) => selectedRow.installmentNo === row.installmentNo)
    );
    setSelectAll(allSelected);
  }, [selectedRows, normalInstallmentList]);

  const calculateTotalAmount = () => {
    let totalAmount = 0;
    if (dpdInstallmentList.length > 0) {
      dpdInstallmentList.forEach((row) => {
        totalAmount += Number(row.totalAmount);
      })
    }

    if (selectedRows.length > 0) {
      selectedRows.forEach((row) => {
        totalAmount += Number(row.totalAmount);
      })
    };
    let formattedSubTotalAmount = Number(totalAmount.toFixed(3));
    setsubTotalAmount(formattedSubTotalAmount);

    let totalAmountWithCharges = parseFloat((totalAmount + Charges).toFixed(3));
    setTotalAmount(totalAmountWithCharges);
  };

  const handlePayment = async () => {
    try {
      const accessToken = sessionStorage.getItem('accessToken');
      if (!accessToken) return;

      if (subTotalAmount <= 0) {
        toast.error("Amount should be greater than zero, Please select atleast one installment.");
        return;
      }

      setIsloading(true);
      const response = await postRequest(
        process.env.REACT_APP_API_URL + apiURL.GET_SESSION_DETAILS, {
        civilId: civilId,
        loanAccountNo: loanAccountNo,
        paymentType: 'RECC',
        amount: TotalAmount,
      });
      setIsloading(false);

      if (response === null) { toast.dismiss(); toast.error(process.env.REACT_APP_ERROR_MSG); return; }
      if (response.status === 401) { toast.error('Session expired'); clearSessionStorage(); setTimeout(() => navigate('/'), 2000); return; }
      if (response.data === '') { toast.dismiss(); toast.error(process.env.REACT_APP_ERROR_MSG); return; }

      const data = response.data;
      if (data.status === 'S' && data.sessionResponse) {
        const redirectUrl = data.sessionResponse?._links?.redirect?.href;
        if (redirectUrl) {
          window.location.href = redirectUrl;
        } else {
          toast.error(process.env.REACT_APP_ERROR_MSG);
        }
      } else if (data.status === 'E') {
        toast.error(data.message ? getLocalizedMessage(data.message) : process.env.REACT_APP_ERROR_MSG);
      } else {
        toast.error(data.message || process.env.REACT_APP_ERROR_MSG);
      }
    } catch (error) {
      setIsloading(false);
      if (axios.isAxiosError(error)) {
        toast.dismiss();
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 401) {
          toast.error('Session expired');
          clearSessionStorage();
          setTimeout(() => navigate('/'), 2000);
        } else {
          toast.error(process.env.REACT_APP_ERROR_MSG);
        }
      }
    }
  }
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  return (
    <Container className='mainContainer' maxWidth="xl" sx={{ mt: { xs: 24, sm: 4 }, mb: 10, minWidth: "375px" }}>
      {isLoading && <Loader />}
      <Box >
        <Box className="headerSubheaderStyle headerColor headerBgColor" sx={{ px: 5 }}>
          <Box className="installmentDetailsHeader pad-right-10"> {t('installments.installmentDetails')} <br />   </Box>
          {/* <Box className="trns-HeaderTitle" sx={{ fontSize: { xs: '0.9rem', sm: '1.2rem' } }}> {name}<span dir="rtl" lang="ar" className='pad-right-10'>{nameArabic}</span> </Box> */}
          <Box className="civilId">  {t('deals.civilId')} <span dir="rtl" lang="ar" ></span> - {civilId} </Box>
        </Box>
        <Box className="selectInstallmentTitle" sx={{ px: 5 }}>
          <Box className="textStyle" >{t('installments.selectInstallments')} <br /></Box>
        </Box>


        <Box className='centerAlign-Table' sx={{ width: '100%', background: '#fff', padding: { xs: '25px 30px', sm: '25px 30px' } }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              mb: 2,
            }}
          >
            <Checkbox
              {...label}
              checked={selectAll}
              onChange={toggleSelectAll}
            />

            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              {t('installments.selectAll')}
            </Typography>
          </Box>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table sx={{ minWidth: { md: 500 } }} size="small" aria-label="simple table">
              <TableHead>
                <TableRow className='tableRow' sx={{
                  "& .MuiTableCell-root": {
                    height: "106px",
                    color: "rgba(0, 16, 75, 0.87)",
                    verticalAlign: "middle",
                  },
                }}>
                  <TableCell sx={{
                    position: { xs: 'sticky', md: 'static' },
                    left: { xs: 0, md: 'auto' },
                    backgroundColor: { xs: '#F8F8F8', sm: '#FFF' },
                    width: { xs: '20px', md: 'auto' }
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {/* <Checkbox
                        {...label}
                        checked={selectAll}
                        onChange={toggleSelectAll}
                      /> */}
                      <div>
                        {t('installments.pay')}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell >{t('installments.installmentNumber')}</TableCell>
                  <TableCell >{t('installments.dueDate')}</TableCell>
                  <TableCell >{t('installments.installmentAmount')}</TableCell>
                  <TableCell >{t('installments.totalAmount')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{
                "& .MuiTableRow-root": {
                  height: "54px",
                },
                "& .MuiTableCell-root": {
                  height: "54px",
                  padding: "0 16px",
                  verticalAlign: "middle",
                  boxSizing: "border-box",
                },
              }}>
                {installmentList.length === 0 && !isLoading && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#888', fontSize: '14px' }}>
                      {t('installments.noDataFound', 'No data found')}
                    </TableCell>
                  </TableRow>
                )}
                {(() => {
                  const maxSelectedInstallmentNo = installmentList.reduce((max, r) => {
                    const isRowChecked = r.selected || r.selectedNew || selectedRows.some((selectedRow) => selectedRow.installmentNo === r.installmentNo);
                    return isRowChecked ? Math.max(max, parseInt(r.installmentNo)) : max;
                  }, 0);
                  return installmentList.map((row) => {
                    const isChecked = row.selected || row.selectedNew || selectedRows.some((selectedRow) => selectedRow.installmentNo === row.installmentNo);
                    const isDisabled = row.selected || (isChecked && parseInt(row.installmentNo) < maxSelectedInstallmentNo);
                    return (
                      <TableRow
                        className='tableRow'
                        key={row.installmentNo}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell sx={{
                          position: { xs: 'sticky', md: 'static' },
                          left: { xs: 0, md: 'auto' },
                          backgroundColor: { xs: '#F8F8F8', sm: '#FFF' },
                          width: { xs: '20px', md: 'auto' }
                        }} component="th" scope="row">
                          {
                            isDisabled ?
                              <Checkbox {...label} disabled checked={isChecked} />
                              :
                              <Checkbox
                                {...label}
                                checked={isChecked}
                                onChange={() => handleCheckboxChange(row)}
                              />
                          }
                        </TableCell>
                        <TableCell >{row.installmentNo}</TableCell>
                        <TableCell >{row.dueDate}</TableCell>
                        <TableCell >
                          <KuwaitiDinarFormat amount={Number(row.installAmount)} />
                        </TableCell>
                        <TableCell >
                          <KuwaitiDinarFormat amount={Number(row.totalAmount)} />
                        </TableCell>
                      </TableRow>
                    );
                  });
                })()}
                {/* <TableRow className="tableRow">
    <TableCell
      sx={{
        position: { xs: "sticky", md: "static" },
        left: { xs: 0, md: "auto" },
        backgroundColor: { xs: "#F8F8F8", sm: "#FFF" },
        width: { xs: "20px", md: "auto" },
      }}
    >
      <Checkbox checked />
    </TableCell>

    <TableCell>INS-1001</TableCell>
    <TableCell>01 Oct 2025</TableCell>
    <TableCell>KWD 120.500</TableCell>
    <TableCell>KWD 150.750</TableCell>
  </TableRow> */}

              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <div className="totalTable" >
          <Box className="totalTableBox" >
            <Table className='insTable' size="small" aria-label="a dense table">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontSize: '14px', color: '#2A92BD', fontWeight: '600' }} align="left">{t('installments.subtotal')}</TableCell>
                  <TableCell sx={{ fontSize: '14px', color: '#00104B', fontWeight: '600' }} align="right">
                    <KuwaitiDinarFormat amount={subTotalAmount} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontSize: '14px', color: '#2A92BD', fontWeight: '600' }} align="left">{t('installments.charges')}</TableCell>
                  <TableCell sx={{ fontSize: '14px', color: '#00104B', fontWeight: '600' }} align="right">
                    <KuwaitiDinarFormat amount={Charges} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontSize: '14px', color: '#2A92BD', fontWeight: '600' }} align="left">{t('installments.total')}</TableCell>
                  <TableCell sx={{ fontSize: '14px', color: '#00104B', fontWeight: '600' }} align="right">
                    <KuwaitiDinarFormat amount={TotalAmount} />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        </div>

        <Box className="insBackButtonBox">
          <Box
            className="justify-item"
            sx={{ display: { xs: "none", sm: 'flex' } }}>
            <Box component="a" className='insLinkStyle' sx={{ cursor: 'pointer' }} onClick={() => {
              if (location.state?.from === "profile") {
                navigate("/home/profile", { state: { activeTab: location.state.activeTab || "deals" } });
              } else {
                navigate(-1);
              }
            }}>
              {t('common.back')}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: "column", sm: "row" }, justifyContent: { xs: 'center', sm: 'space-between' }, alignItems: 'center', width: '250px' }}>
              <Box>
              </Box>
              <Button
                variant="contained"
                disabled={subTotalAmount <= 0}
                onClick={() => setModalOpen(true)}
                // onClick={handlePayment}
                className='payLink'
                sx={{
                  fontSize: "14px",
                  fontWeight: 500,
                  fontFamily: "Roboto",
                  "&.Mui-disabled": {
                    cursor: "not-allowed",
                    pointerEvents: "auto",
                  },
                }}>
                {t('common.pay')}
              </Button>
            </Box>
          </Box>
        </Box>
        <ChangeCardModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onContinueRegistered={() => { setModalOpen(false); handlePayment(); }}
          cardNo={cardNo}
          isLoading={isLoading}
          civilId={civilId}
          loanAccountNo={loanAccountNo}
          totalAmount={TotalAmount}
        />

      </Box>
      <ToastContainer
        position="top-right"
        newestOnTop
        limit={1}
        autoClose={toastCloseInMs}
      />
      <IdleTimerContainer />
    </Container>
  )
};
export default Installments; 